/* eslint-disable @typescript-eslint/ban-ts-comment */
//@ts-nocheck
//TODO: fix ts moments
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { FC, useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Center, Flex, Loader, Text, noop } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AppRoutes } from '@/router/routes.ts';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { KEYS } from '@/entities/Booking/api/queries.tsx';
import { BookingMeetingRoomFields } from '@/entities/Booking/components/BookingMeetingRoomFields.tsx';
import { CarPassSelector } from '@/entities/CarPassSelector/CarPassSelector';
import { RenterSelector } from '@/entities/RenterSelector';

import { LocationSelectorSheet } from '@/features/LocationSelectorSheet/LocationSelectorSheet';
import { TMeetingRoomForm } from '@/features/NewBooking/useMeetingRoomForm.tsx';
import { PlaceMap } from '@/features/PlaceMap';
import { PlaceSelectorSheet } from '@/features/PlaceSelectorSheet';

import { bookingApi } from '@/shared/api/booking';
import { bookingObjectsApi } from '@/shared/api/bookingObjects';
import { TODAY } from '@/shared/constant/app';
import { carNumberToLatin } from '@/shared/helpers/normalizeCarNumber';
import { getObjectHierarchy } from '@/shared/helpers/utils.ts';
import { useBookingEditForm } from '@/shared/hooks/useBookingEditForm.tsx';
import { useGetBookingTypeData } from '@/shared/hooks/useGetBookingTypeData';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store';
import useBookingStore from '@/shared/store/booking.store.ts';
import { useSettingsStore } from '@/shared/store/settings.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { IRequestError } from '@/shared/types/app.types';
import { IBookingObjects } from '@/shared/types/bookingObjects.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { ContentCheckbox } from '@/shared/uikit/components/checkbox/contentCheckbox';
import { NetworkError } from '@/shared/uikit/components/error/networkError';
import { LabelInput } from '@/shared/uikit/components/input';
import { CarInput } from '@/shared/uikit/components/input/CarInput/CarInput.tsx';
import { DateTimePicker } from '@/shared/uikit/components/picker/DateTimePicker/DateTimePicker';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { exclusiveEnd, getNearestDate } from '@/utils/date';

type EditBookingProps = {
	id?: string;
	open?: boolean;
	close?: () => void;
};

export const EditBooking: FC<EditBookingProps> = ({ id = null, open, close }) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const { id: queryId = null } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const form = useBookingEditForm();
	const { userRate } = useContext(AppContext);
	const defaultCarNumber = useSettingsStore((state) => state.carNumber);
	const carPasses = useUserStore((state) => state.carPasses);
	const { bookingPlace, setBookingPlace } = useBookingStore();

	const {
		data: booking = null,
		isLoading: isBookingLoading,
		isError: isBookingLoadingError,
	} = useQuery({
		queryKey: [KEYS.BOOKING],
		queryFn: () =>
			bookingApi
				.getBookingById(id ?? queryId ?? '')
				.then((res) => ({
					...res,
					start: dayjs(res.start).toDate(),
					end: dayjs(res.end).toDate(),
				}))
				.then((res) => {
					const { booking_object, start, end, client, pass, ...rest } = res;

					form.setValues({
						...rest,
						date: [start, end],
						kind: booking_object.type.kind,
						...(pass
							? { pass_id: Number(carPasses.find((pass) => pass.label === rest.car_number)?.value) }
							: {}),
						...(rest.is_guest ? { car_number: rest.car_number } : {}),
					});
					return res;
				}),
		retry: 0,
	});

	const {
		data: objectParents = null,
		isLoading: isObjectParentsLoading,
		isError: isObjectParentsError,
	} = useQuery({
		queryKey: ['objectParents', booking],
		queryFn: () => bookingObjectsApi.getObjectParents(booking?.booking_object.id!),
		enabled: !!booking,
	});

	const { mutate: editBooking, isPending } = useMutation({
		mutationFn: bookingApi.editBooking,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: [KEYS.BOOKINGS] });
			void queryClient.invalidateQueries({ queryKey: [KEYS.BOOKING] });
			useShowNotifaction({ text: 'Бронирование обновлено', type: 'success' });

			if (isMobile && close) {
				close();
			} else {
				navigate(AppRoutes.Bookings, { replace: true });
			}
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data.message ?? 'Ошибка клиент-серверного взаимодействия',
				type: 'error',
			});
		},
	});

	const [openedLocationSelector, { close: closeLocationSelector, open: openLocationSelector }] =
		useDisclosure(false);

	const [openedPlaceSelector, { close: closePlaceSelector, open: openPlaceSelector }] =
		useDisclosure(false);

	const handleChangePlace = (place: IBookingObjects) => {
		form.setFieldValue('booking_object_id', place?.id);
		setBookingPlace(place);
	};

	const handleEditBooking = () => {
		if (form.isValid()) {
			const { date, ...rest } = form.getTransformedValues();
			editBooking({
				bookingId: Number(id ?? queryId),
				payload: {
					...rest,
					start: date[0],
					end: exclusiveEnd(date[1]),
				},
			});
			setBookingPlace(null);
		}
	};

	const actions = () => {
		// if (currentBookingLoad && currentBookingLoad >= 100) {
		// 	return [
		// 		{
		// 			children: (
		// 				<Flex direction="column">
		// 					<Text fw={700} fz={18} lh={'24px'}>
		// 						Нет свободных мест
		// 					</Text>
		// 				</Flex>
		// 			),
		// 			color: 'red',
		// 			variant: 'light',
		// 		},
		// 	];
		// }

		return [
			{
				children: 'Сохранить изменения',
				...(form.isValid()
					? { loading: isPending, onClick: handleEditBooking }
					: { variant: 'light' }),
			},
		];
	};

	if (isBookingLoading || isObjectParentsLoading)
		return (
			<LayoutRow
				title={{
					title: { children: 'Редактирование бронирования', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<Center h="100%">
					<Loader />
				</Center>
			</LayoutRow>
		);

	if (isBookingLoadingError)
		return (
			<LayoutRow
				title={{
					title: { children: 'Редактирование бронирования', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<NetworkError />
			</LayoutRow>
		);

	const bookingType = useGetBookingTypeData({
		bookingType: booking!.booking_object.type.kind ?? 'work_place',
	});

	const handleSetIsShowCarNumberField = (value: boolean) => {
		(form as TMeetingRoomForm).setFieldValue('show_auto', value);
	};

	const content = booking && (
		<>
			<Flex direction="column" gap={24}>
				<ContentCheckbox label={bookingType?.label} leftIcon={bookingType?.icon} checked />

				<DateTimePicker
					defaultTime={
						dayjs(booking?.start).isToday()
							? getNearestDate(TODAY).toDate()
							: dayjs(booking?.start).toDate()
					}
					disabledDates={{
						minDate: TODAY.toDate(),
						// maxDate:
						// 	booking.booking_object.type.kind === 'parking_place'
						// 		? TODAY.add(userRate?.maxParkingDays, 'days').toDate()
						// 		: TODAY.add(userRate?.maxDefaultDays, 'days').toDate(),
						maxDate:
							booking?.booking_object.type.kind === 'parking_place'
								? TODAY.add(14, 'days').toDate()
								: TODAY.add(30, 'days').toDate(),
					}}
					{...form.getInputProps('date')}
					onlyTime
				/>

				<Flex direction="column" gap={24}>
					<Flex direction="column" gap={16}>
						<Text fz={20} fw={800} lh="26px">
							Локация
						</Text>

						<LabelInput
							label="Офис и этаж"
							placeholder="Не выбрано"
							type="lock"
							// onClick={openLocationSelector}
							defaultValue={objectParents ? getObjectHierarchy(objectParents) : ''}
							multiple
							readOnly
						/>
					</Flex>

					<Flex direction="column" gap={16}>
						<Flex gap={8}>
							<LabelInput
								label={bookingType?.label}
								type="lock"
								placeholder="Выбрать из списка"
								defaultValue={(bookingPlace?.name ?? booking?.booking_object.name ?? '').replace(
									/\s\|\s[\d\D]+/gm,
									'',
								)}
								// onClick={openPlaceSelector}
								readOnly
							/>
						</Flex>

						{form.values.is_guest && (
							<RenterSelector
								value={form.values.guests?.[0]?.renter_id ?? null}
								onChange={(renterId) => {
									const guests = form.values.guests ?? [];

									if (guests.length > 0) {
										form.setFieldValue('guests.0.renter_id', renterId);
										return;
									}

									form.setFieldValue('guests', [
										{
											full_name: '',
											email: '',
											phone: '',
											pass_type: '',
											visit_reason: '',
											renter_id: renterId,
										},
									]);
								}}
							/>
						)}

						{bookingType.type === 'parking_place' &&
							(form.values.is_guest ? (
								<CarInput
									value={carNumberToLatin(form.values.car_number ?? booking?.car_number)}
									onChange={(carNumber) =>
										form.setFieldValue('car_number', String(carNumber).toUpperCase())
									}
								/>
							) : (
								<CarPassSelector
									carPasses={carPasses}
									value={String(form.values.pass_id)}
									onChange={(pass_id) => {
										form.setValues({
											...form.values,
											car_number: carPasses?.find((pass) => pass.value === pass_id)?.label,
											pass_id: Number(pass_id),
										});
									}}
								/>
							))}

						{bookingType.type === 'meeting_room' && (
							<BookingMeetingRoomFields
								form={form}
								isExternalMeeting={booking?.booking_object?.is_external}
								isShowCarNumberField={
									(form as UseFormReturnType<TMeetingRoomForm>).getInputProps('show_auto').value
								}
								onExternalMeeting={noop}
								onServiceRequired={noop}
								onShowCarNumberField={handleSetIsShowCarNumberField}
							/>
						)}
					</Flex>
				</Flex>

				{!isMobile && (
					<PlaceMap
						parent={booking?.booking_object}
						objectType={booking.booking_object.type.kind}
						isSingle
					/>
				)}
			</Flex>

			<LocationSelectorSheet
				title="Локация"
				open={openedLocationSelector}
				close={closeLocationSelector}
			/>

			{openedPlaceSelector && (
				<PlaceSelectorSheet
					type={bookingType.type}
					date={form.values?.date}
					open={openedPlaceSelector}
					close={closePlaceSelector}
					onSave={handleChangePlace}
				/>
			)}
		</>
	);

	if (isMobile)
		return (
			<BottomSheet
				opened={open}
				onClose={close}
				title="Бронирование"
				withCloseButton={false}
				buttons={actions()}
				withBackButton
				isFullScreen
			>
				{content}
			</BottomSheet>
		);

	return (
		<LayoutRow
			title={{
				title: { children: 'Редактирование бронирования', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)', overflow: 'hidden' }}
			buttons={actions()}
		>
			<Box px={16} style={{ overflowY: 'auto' }}>
				{content}
			</Box>
		</LayoutRow>
	);
};
